import { conversationRequestScope } from './requestScope';
import { attentionState } from './state';
import { reconcileHistory } from './messageHistory';
import types from 'dashboard/store/mutation-types';

// Mounted list owners supply their actual current native/filter query. Offers
// are cheap and frequent; record fetching happens only after a changed revision
// or a failed attempt. One flight, one latest offer and bounded burst timers.
export function createConversationListRecovery(store, selection) {
  const owner = Symbol('native-list-owner');
  let timer = null;
  let deadline = null;
  let flight = null;
  let pending = null;
  let completed = null;
  let disposed = false;
  // A late realtime frame can corrupt native metadata after an authoritative
  // recovery without advancing the server revision. Detect that local change
  // too; selected transcript/history changes do not trigger a list fetch.
  const localSignature = () => JSON.stringify([store.state.conversations?.attentionMetadataGeneration || 0, (store.state.conversations?.allConversations || []).map(row =>
    [row.id, row.inbox_id, row.status, row.meta?.assignee?.id, row.meta?.assignee_type, row.meta?.team?.id,
      row.priority, row.updated_at, row.last_activity_at, row.labels, row.custom_attributes,
      row.additional_attributes, row.snoozed_until, row.first_reply_created_at, row.waiting_since,
      row.meta?.sender]).sort((a, b) => a[0] - b[0])]);
  const clear = () => { clearTimeout(timer); clearTimeout(deadline); timer = deadline = null; };
  const capture = revision => {
    const context = { state: store.state.conversations, rootState: store.state, rootGetters: store.getters };
    const scope = conversationRequestScope(context);
    const query = selection();
    if (!scope() || !query) return null;
    const signature = JSON.stringify(query);
    const current = () => !disposed && scope() && signature === JSON.stringify(selection());
    const cacheSignature = localSignature();
    return { ...query, revision, signature, cacheSignature, cacheCurrent: () => cacheSignature === localSignature(), route: store.state.route, current };
  };
  const execute = async () => {
    clear();
    if (!pending || disposed) return;
    if (flight?.current()) return;
    const offer = pending; pending = null;
    if (!offer.current()) return;
    const active = offer; flight = active;
    attentionState.listRecovery = { account: store.getters.getCurrentAccountId, actor: store.getters.getCurrentUser.id, status: 'Recovering current queue', owner };
    try {
      const accepted = await store.dispatch('reconcileConversationList', offer);
      if (accepted && offer.current()) {
        completed = { ...offer, cacheSignature: localSignature() };
        if (attentionState.listRecovery?.owner === owner) attentionState.listRecovery.status = 'Current queue reconciled';
      }
    } catch {
      if (offer.current() && attentionState.listRecovery?.owner === owner) attentionState.listRecovery.status = 'Queue recovery failed; retrying on sync';
      // Keep the revision dirty. The next authoritative offer retries.
    }
    finally {
      if (flight === active) { flight = null; if (pending) execute(); }
    }
  };
  return {
    offer(revision) {
      if (disposed || typeof revision !== 'string' || !/^[a-f0-9]{64}$/.test(revision)) return;
      const next = capture(revision);
      if (!next) return;
      if (completed?.current() && completed.revision === revision && completed.signature === next.signature && completed.cacheSignature === next.cacheSignature) return;
      if (flight?.current() && flight.revision === revision && flight.signature === next.signature && flight.cacheSignature === next.cacheSignature && !pending) return;
      pending = next;
      clearTimeout(timer); timer = setTimeout(execute, 150);
      if (deadline === null) deadline = setTimeout(execute, 750);
    },
    dispose() { disposed = true; pending = null; completed = null; clear(); if (attentionState.listRecovery?.owner === owner) attentionState.listRecovery = null; },
  };
}

// No transcript scroll, read/ACK, page reset, filter change or draft mutation.
// Fresh records and metadata are authorized before this mutation is called.
export function mergeRecoveredConversationList(state, { records, metadata, requestedIds }) {
  const available = new Set(metadata.map(row => row.id));
  const requested = new Set(requestedIds);
  const removed = new Set([...requested].filter(id => !available.has(id)));
  const next = state.allConversations.filter(row => !removed.has(row.id));
  const positions = new Map(next.map((row, index) => [row.id, index]));
  const merge = (row, full) => {
    const index = positions.get(row.id);
    if (index === undefined) {
      if (full && available.has(row.id)) { positions.set(row.id, next.length); next.push(row); }
      return;
    }
    const old = next[index];
    // Authoritative current state can have an older application timestamp
    // after a delayed transaction commits. The request's mutation-generation
    // fence rejects concurrent local updates before this atomic merge.
    const meta = { ...old.meta, ...row.meta };
    for (const key of ['sender', 'assignee', 'team']) {
      if (row.meta?.[key]?.id && row.meta[key].id === old.meta?.[key]?.id &&
          (key !== 'assignee' || row.meta.assignee_type === old.meta.assignee_type)) {
        meta[key] = { ...old.meta[key], ...row.meta[key] };
      }
    }
    next[index] = {
      ...old, ...row, meta,
      messages: full ? reconcileHistory(old.messages || [], row.messages || []) : old.messages,
      dataFetched: old.dataFetched, allMessagesLoaded: old.allMessagesLoaded,
    };
  };
  records.forEach(row => merge(row, true));
  metadata.forEach(row => merge(row, false));
  state.allConversations = next;
  for (const id of removed) { delete state.attachments[id]; delete state.syncConversationsMessages[id]; }
  if (removed.has(state.selectedChatId)) state.selectedChatId = null;
}

// O(1) monotonic fence for native metadata mutations, including A→B→A.
// Loading older transcript pages and changing composer drafts do not advance it.
export function trackConversationListMutations(mutations) {
  const names = ['SET_ALL_CONVERSATION', 'EMPTY_ALL_CONVERSATION', 'ASSIGN_AGENT', 'ASSIGN_TEAM',
    'UPDATE_CONVERSATION_LAST_ACTIVITY', 'ASSIGN_PRIORITY', 'UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES',
    'CHANGE_CONVERSATION_STATUS', 'ADD_MESSAGE', 'ADD_CONVERSATION', 'DELETE_CONVERSATION',
    'UPDATE_CONVERSATION', 'UPDATE_MESSAGE_UNREAD_COUNT', 'UPDATE_ASSIGNEE',
    'UPDATE_CONVERSATION_CONTACT', 'CLEAR_CONTACT_CONVERSATIONS', 'SET_CONVERSATION_CAN_REPLY'];
  const tracked = new Set(['RECONCILE_ATTENTION_CONVERSATION_LIST', ...names.map(name => types[name]).filter(Boolean)]);
  return Object.fromEntries(Object.entries(mutations).map(([name, mutation]) => [name, tracked.has(name)
    ? (state, ...args) => { const result = mutation(state, ...args); state.attentionMetadataGeneration = (state.attentionMetadataGeneration || 0) + 1; return result; }
    : mutation]));
}
